import React, { useState, useEffect } from 'react';
import { useChargeOptions } from '@/contexts/ChargeOptionsContext';
import { Plan, User } from '@/types';
import { StripePaymentForm } from './StripePaymentForm';
import { SavedCardsPayment } from './SavedCardsPayment';
import { MockPaymentForm } from './MockPaymentForm';
import { useToast } from '@/contexts/ToastContext'; // Added
import { ClipboardDocumentListIcon, CreditCardIcon } from '@/components/icons/HeroIcons'; // Added
import { AISHA_API_BASE } from '../../constants';

interface ChargeOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPlan: (plan: Plan) => void;
  currentUser: User | null;
  onPaymentHistoryClick?: () => void; // Added
}

export const ChargeOptionsModal: React.FC<ChargeOptionsModalProps> = ({
  isOpen,
  onClose,
  onSelectPlan,
  currentUser,
  onPaymentHistoryClick, // Added
}) => {
  const { showToast } = useToast(); // Added
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [savedCards, setSavedCards] = useState<any[]>([]);
  const [showSavedCards, setShowSavedCards] = useState(false);
  const [showNewCardForm, setShowNewCardForm] = useState(false);
  const [loadingSavedCards, setLoadingSavedCards] = useState(false);
  const [showCardManagement, setShowCardManagement] = useState(false);
  const [setupClientSecret, setSetupClientSecret] = useState<string | null>(null);
  const [isCreatingSetup, setIsCreatingSetup] = useState(false);
  const [cardRegistrationSuccess, setCardRegistrationSuccess] = useState(false);
  
  // 全てのHooksを最上部で呼び出す
  const chargeOptions = useChargeOptions()?.filter((option) => option.isActive);

  // 保存済みカードを取得
  useEffect(() => {
    if (currentUser && isOpen) {
      fetchSavedCards();
    }
  }, [currentUser, isOpen]);

  const fetchSavedCards = async () => {
    if (!currentUser) return;
    
    setLoadingSavedCards(true);
    try {
      const response = await fetch(`${AISHA_API_BASE}/stripe/saved-cards/?user_id=${currentUser.id}`);
      const data = await response.json();
      
      if (data.success) {
        setSavedCards(data.payment_methods || []);
      }
    } catch (error) {
      console.error('Failed to fetch saved cards:', error);
    } finally {
      setLoadingSavedCards(false);
    }
  };

  const handleStartCardRegistration = async () => {
    if (!currentUser) return;
    setIsCreatingSetup(true);
    setCardRegistrationSuccess(false);
    try {
      const response = await fetch(`${AISHA_API_BASE}/stripe/setup-intent/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: currentUser.id })
      });
      const data = await response.json();
      if (data.success) {
        setSetupClientSecret(data.client_secret);
      } else {
        showToast('error', data.error || 'カード登録の準備に失敗しました');
      }
    } catch (error) {
      showToast('error', 'カード登録の準備に失敗しました');
    } finally {
      setIsCreatingSetup(false);
    }
  };

  // 関数定義を上に移動
  const handleModalClose = () => {
    setShowPaymentForm(false);
    setShowSavedCards(false);
    setShowNewCardForm(false);
    setShowCardManagement(false);
    setSetupClientSecret(null);
    setCardRegistrationSuccess(false);
    setSelectedPlan(null);
    onClose();
  };

  if (!isOpen) return null;

  // ユーザーがログインしていない場合
  if (!currentUser) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 pb-20">
        <div className="bg-gray-800 p-8 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
          <h2 className="text-2xl font-semibold mb-6 text-center text-indigo-400">
            ログインが必要です
          </h2>
          <p className="text-gray-300 mb-6 text-center">
            クレジットをチャージするには、まずログインしてください。
          </p>
          <button
            onClick={handleModalClose}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-6 rounded-lg transition duration-150 ease-in-out"
          >
            閉じる
          </button>
        </div>
      </div>
    );
  }

  // APIにより取得したデータ
  // 有効なオプションのみ表示
  
  // チャージオプションが取得できない場合は固定プランを使用
  const defaultChargeOptions = [
    {
      id: 1,
      name: '500クレジット',
      priceYen: 500,
      creditsAwarded: 500,
      creditsBonus: 0,
      isActive: true
    },
    {
      id: 2,
      name: '1100クレジット',
      priceYen: 1000,
      creditsAwarded: 1100,
      creditsBonus: 100,
      isActive: true
    }
  ];
  
  const displayOptions = chargeOptions && chargeOptions.length > 0 ? chargeOptions : defaultChargeOptions;

  const handlePlanSelect = (option: any) => {
    const plan: Plan = {
      id: option.id,
      name: option.name,
      price: option.priceYen,
      credits: option.creditsAwarded,
    };
    setSelectedPlan(plan);
    
    // 保存済みカードがある場合は選択画面を表示
    if (savedCards.length > 0) {
      setShowSavedCards(true);
    } else {
      setShowPaymentForm(true);
      setShowNewCardForm(true);
    }
  };

  const handlePaymentSuccess = (creditBalance: number) => {
    console.log('決済成功！新しいクレジット残高:', creditBalance);
    // 成功通知を表示
    showToast('success', `決済が完了しました！${selectedPlan?.credits}クレジットが追加されました。`);
    // 成功時の処理
    if (selectedPlan) {
      onSelectPlan(selectedPlan);
    }
    setShowPaymentForm(false);
    setSelectedPlan(null);
    onClose();
  };

  const handlePaymentError = (error: string) => {
    console.error('決済エラー:', error);
    // toast通知でエラーを表示
    const errorMessage = error.includes('Your card was declined') 
      ? 'カードが拒否されました。別のカードをお試しください。'
      : error.includes('Your card number is incomplete')
      ? 'カード番号が不完全です。正しいカード番号を入力してください。'
      : error.includes('expiration date is incomplete')
      ? 'カードの有効期限が不完全です。MM/YY形式で入力してください。'
      : error.includes('security code is incomplete')
      ? 'セキュリティコードが不完全です。カード裏面の3桁または4桁の番号を入力してください。'
      : error.includes('Failed to create charge')
      ? '決済の初期化に失敗しました。しばらく待ってから再度お試しください。'
      : `決済エラー: ${error}`;
    
    showToast('error', errorMessage);
  };

  const handlePaymentCancel = () => {
    setShowPaymentForm(false);
    setShowNewCardForm(false);
    setShowSavedCards(false);
    setSelectedPlan(null);
  };

  const handleUseNewCard = () => {
    setShowSavedCards(false);
    setShowNewCardForm(true);
    setShowPaymentForm(true);
  };

  const handleBackToCardSelection = () => {
    setShowNewCardForm(false);
    setShowPaymentForm(false);
    setShowSavedCards(true);
  };

  // 保存済みカード選択画面
  if (showSavedCards && selectedPlan) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 pb-20">
        <div className="bg-gray-800 p-8 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <h2 className="text-2xl font-semibold mb-6 text-center text-indigo-400">
            支払い方法を選択
          </h2>
          
          <div className="mb-4 p-4 bg-gray-700 rounded-lg">
            <h3 className="text-lg font-medium text-indigo-300">
              {selectedPlan.name}
            </h3>
            <p className="text-gray-400">
              ¥{selectedPlan.price} で {selectedPlan.credits} クレジット
            </p>
          </div>
          
          <SavedCardsPayment
            userId={currentUser?.id || 'anonymous'}
            totalAmount={selectedPlan.price}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentError={handlePaymentError}
            onUseNewCard={handleUseNewCard}
            onCancel={handlePaymentCancel}
            savedCards={savedCards}
            onCardsUpdate={fetchSavedCards}
            paymentType="credit_charge"
            creditAmount={selectedPlan.credits}
          />
        </div>
      </div>
    );
  }

  // 新規カード入力画面
  if (showNewCardForm && selectedPlan) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 pb-20">
        <div className="bg-gray-800 p-8 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <h2 className="text-2xl font-semibold mb-6 text-center text-indigo-400">
            クレジット決済
          </h2>
          
          {savedCards.length > 0 && (
            <button
              onClick={handleBackToCardSelection}
              className="mb-4 text-indigo-400 hover:text-indigo-300 text-sm flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              保存済みカードに戻る
            </button>
          )}
          
          <StripePaymentForm
            userId={currentUser?.id || 'anonymous'}
            chargeAmount={selectedPlan.price}
            creditAmount={selectedPlan.credits}
            onSuccess={(creditBalance) => {
              fetchSavedCards(); // カードリストを更新
              handlePaymentSuccess(creditBalance);
            }}
            onError={handlePaymentError}
            onCancel={handlePaymentCancel}
          />
        </div>
      </div>
    );
  }

  // カード管理画面
  if (showCardManagement) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 pb-20">
        <div className="bg-gray-800 p-8 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <h2 className="text-2xl font-semibold mb-6 text-center text-indigo-400">
            カード管理
          </h2>

          {/* 登録済みカード一覧 */}
          {loadingSavedCards ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400"></div>
            </div>
          ) : savedCards.length > 0 ? (
            <div className="space-y-2 mb-6">
              {savedCards.map((card) => (
                <div key={card.id} className="p-4 rounded-lg border border-gray-600 bg-gray-700 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-lg">💳</span>
                    <div>
                      <span className="text-white font-medium">{card.brand} ****{card.last4}</span>
                      {card.is_default && (
                        <span className="ml-2 px-2 py-0.5 bg-indigo-600 text-white text-xs rounded">デフォルト</span>
                      )}
                      <p className="text-sm text-gray-400">有効期限: {card.exp_month}/{card.exp_year}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      if (confirm('このカードを削除しますか？')) {
                        fetch(`${AISHA_API_BASE}/stripe/saved-cards/delete/?payment_method_id=${card.id}`, { method: 'DELETE' })
                          .then(() => fetchSavedCards());
                      }
                    }}
                    className="text-gray-400 hover:text-red-400"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 mb-6">
              <p className="text-gray-400">登録済みのカードはありません</p>
            </div>
          )}

          {/* カード登録フォーム */}
          {cardRegistrationSuccess ? (
            <div className="text-center py-4 mb-4">
              <div className="text-green-400 text-lg font-medium mb-2">カードを登録しました</div>
              <button
                onClick={() => {
                  setCardRegistrationSuccess(false);
                  setSetupClientSecret(null);
                  fetchSavedCards();
                }}
                className="text-indigo-400 hover:text-indigo-300 text-sm"
              >
                続けて別のカードを登録
              </button>
            </div>
          ) : setupClientSecret ? (
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-300 mb-3">新しいカードを登録</h3>
              <StripePaymentForm
                userId={currentUser?.id || ''}
                chargeAmount={0}
                creditAmount={0}
                existingClientSecret={setupClientSecret}
                onSuccess={() => {
                  setCardRegistrationSuccess(true);
                  fetchSavedCards();
                  showToast('success', 'カードを登録しました');
                }}
                onError={(error) => {
                  showToast('error', `カード登録エラー: ${error}`);
                }}
                onCancel={() => {
                  setSetupClientSecret(null);
                }}
              />
            </div>
          ) : (
            <button
              onClick={handleStartCardRegistration}
              disabled={isCreatingSetup}
              className="w-full mb-4 p-3 border-2 border-dashed border-gray-600 rounded-lg text-gray-400 hover:text-white hover:border-indigo-500 transition-all flex items-center justify-center"
            >
              <CreditCardIcon className="w-5 h-5 mr-2" />
              {isCreatingSetup ? '準備中...' : '新しいカードを登録'}
            </button>
          )}

          <button
            onClick={() => {
              setShowCardManagement(false);
              setSetupClientSecret(null);
              setCardRegistrationSuccess(false);
            }}
            className="w-full bg-gray-600 hover:bg-gray-700 text-gray-200 font-bold py-3 px-6 rounded-lg transition duration-150 ease-in-out"
          >
            戻る
          </button>
        </div>
      </div>
    );
  }

  // プラン選択画面
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 pb-20">
      <div className="bg-gray-800 p-8 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-semibold mb-6 text-center text-indigo-400">
          クレジットをチャージ
        </h2>
        
        {/* カード管理・決済履歴ボタン */}
        <div className="mb-4 space-y-2">
          <button
            onClick={() => {
              setShowCardManagement(true);
              fetchSavedCards();
            }}
            className="w-full bg-gray-700 hover:bg-gray-600 text-gray-200 font-medium py-2 px-4 rounded-lg transition duration-150 ease-in-out flex items-center justify-center"
          >
            <CreditCardIcon className="w-5 h-5 mr-2" />
            カード管理
            {savedCards.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-indigo-600 text-white text-xs rounded-full">{savedCards.length}</span>
            )}
          </button>
          {currentUser && onPaymentHistoryClick && (
            <button
              onClick={onPaymentHistoryClick}
              className="w-full bg-gray-700 hover:bg-gray-600 text-gray-200 font-medium py-2 px-4 rounded-lg transition duration-150 ease-in-out flex items-center justify-center"
            >
              <ClipboardDocumentListIcon className="w-5 h-5 mr-2" />
              決済履歴を確認
            </button>
          )}
        </div>
        
        <div className="space-y-4">
          {displayOptions?.map((option) => (
            <div
              key={option.id}
              className="p-4 border border-gray-700 rounded-lg bg-gray-700/50"
            >
              <h3 className="text-lg font-medium text-indigo-300">
                  {(option as any).displayInfo || `${option.creditsAwarded} クレジット`}
                  {(option.creditsBonus || 0) > 0 && (
                  <span className="text-sm text-yellow-400 font-semibold ml-2">
                    (+{option.creditsBonus} お得!)
                  </span>
                )}
              </h3>
              <p className="text-sm text-gray-400 mb-3">
                ¥{option.priceYen} で購入
              </p>
              <button
                onClick={() => handlePlanSelect(option)}
                className="mt-2 w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-150 ease-in-out"
              >
                ¥{option.priceYen} チャージ
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={handleModalClose}
          className="mt-6 w-full bg-gray-600 hover:bg-gray-700 text-gray-200 font-bold py-3 px-6 rounded-lg transition duration-150 ease-in-out"
        >
          閉じる
        </button>
      </div>
    </div>
  );
};
